
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Battery, BatteryFull, BatteryLow, BatteryWarning } from 'lucide-react';

interface QuotaData {
  date: string;
  quota_used: number;
  last_updated: string;
}

const MAX_DAILY_QUOTA = 10000; // YouTube API daily quota limit

const QuotaUsageDisplay = () => {
  const [quotaData, setQuotaData] = useState<QuotaData | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchQuotaData = async () => {
      try {
        setLoading(true);
        
        // Get today's date in the format YYYY-MM-DD
        const today = new Date().toISOString().split('T')[0];
        
        // Fetch the quota usage for today
        const { data, error } = await supabase
          .from('youtube_api_quota')
          .select('*')
          .eq('date', today)
          .single();
        
        if (error) {
          console.error('Error fetching quota data:', error);
        } else if (data) {
          // Explicitly type the data as QuotaData to avoid type issues
          const quotaInfo: QuotaData = {
            date: data.date,
            quota_used: data.quota_used,
            last_updated: data.last_updated
          };
          setQuotaData(quotaInfo);
        }
      } catch (error) {
        console.error('Failed to fetch quota data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchQuotaData();
    
    // Refresh every 5 minutes
    const intervalId = setInterval(fetchQuotaData, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  const getPercentage = () => {
    if (!quotaData) return 0;
    return Math.min(100, Math.round((quotaData.quota_used / MAX_DAILY_QUOTA) * 100));
  };
  
  const getStatusIcon = () => {
    const percentage = getPercentage();
    
    if (percentage >= 90) {
      return <BatteryWarning className="h-5 w-5 text-red-500" />;
    } else if (percentage >= 70) {
      return <BatteryLow className="h-5 w-5 text-amber-500" />;
    } else if (percentage >= 30) {
      return <Battery className="h-5 w-5 text-green-500" />;
    } else {
      return <BatteryFull className="h-5 w-5 text-green-500" />;
    }
  };
  
  const getStatusColor = () => {
    const percentage = getPercentage();
    
    if (percentage >= 90) {
      return 'text-red-500';
    } else if (percentage >= 70) {
      return 'text-amber-500';
    } else {
      return 'text-green-500';
    }
  };
  
  const getFormattedTime = () => {
    if (!quotaData) return 'Jamais';
    
    try {
      const updateTime = new Date(quotaData.last_updated);
      return updateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'Format invalide';
    }
  };
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium flex items-center gap-2">
          {getStatusIcon()}
          <span>Quota API YouTube</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse flex flex-col space-y-3">
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
          </div>
        ) : quotaData ? (
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
              <span>Utilisation aujourd'hui</span>
              <span className={getStatusColor()}>{getPercentage()}%</span>
            </div>
            <Progress value={getPercentage()} className="h-2" />
            <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 mt-1">
              <span>{quotaData.quota_used} / {MAX_DAILY_QUOTA} unités</span>
              <span>Mis à jour à {getFormattedTime()}</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Le quota YouTube se réinitialise tous les jours à minuit Pacific Time (PT).
              <br />
              <strong>Données réelles</strong> basées sur l'utilisation effective de votre clé API.
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aucune donnée d'utilisation disponible pour aujourd'hui.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default QuotaUsageDisplay;
